import { useSyncExternalStore } from 'react';

/**
 * «Тема» — «Системная», «Светлая» or «Тёмная», as the mobile rider app's
 * `themeModeProvider` has it.
 *
 * The stylesheet answers «Системная» by itself, from `prefers-color-scheme`; the
 * other two are an attribute on `<html>` that outranks the media query either way.
 * So the page is drawn in the right theme before a line of this runs, and the only
 * thing this module does at start is to say so to the browser chrome: the colour
 * of Safari's bar and of Android's status bar, which no stylesheet reaches.
 *
 * Kept on this device, as the phone keeps it: it is how this screen should look,
 * not something about the passenger's account.
 */
export type ThemeMode = 'system' | 'light' | 'dark';

const KEY = 'limuzin.customer.theme';
const listeners = new Set<() => void>();
const night = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

function read(): ThemeMode {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // A private window. Nothing was chosen here, so the handset decides.
  }
  return 'system';
}

let mode: ThemeMode = read();

/** Whether the page is dark this moment — the mode, or the handset answering for it. */
export function isDark(): boolean {
  return mode === 'dark' || (mode === 'system' && (night?.matches ?? false));
}

/** The attribute, and the two things outside the page that follow it. */
function apply(): void {
  const root = document.documentElement;
  if (mode === 'system') delete root.dataset.theme;
  else root.dataset.theme = mode;
  const dark = isDark();
  // The ground, so the bar above the page is the page's own colour.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#131614' : '#f6f4ef');
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', dark ? 'dark' : 'light');
  listeners.forEach((heard) => heard());
}

/** Called once, before the first render — `main.tsx`. */
export function startTheme(): void {
  apply();
  // The handset turned over at dusk. Nothing changes unless «Тема» is «Системная».
  night?.addEventListener('change', () => {
    if (mode === 'system') apply();
  });
}

export function chooseTheme(next: ThemeMode): void {
  mode = next;
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // Storage refused: the choice lasts as long as the tab.
  }
  apply();
}

function subscribe(heard: () => void): () => void {
  listeners.add(heard);
  return () => listeners.delete(heard);
}

/** The mode «Тема» is left on. */
export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(subscribe, () => mode);
}

/** Whether the page is being drawn dark — which of the two daylight streets to show. */
export function useDark(): boolean {
  return useSyncExternalStore(subscribe, isDark);
}
